import { Filter, Calendar as CalendarIcon, Clock, MapPin, ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import CustomSelect from '../../../../components/forms/CustomSelect';
import EmptyStateTable from '../../../../components/feedback/EmptyStateTable';
import TableSkeleton from '../../../../components/feedback/TableSkeleton';
import BookingActions from './BookingActions';
import appBookingsData from '../../../../data/appBookings/appBookingsData.json';
import { getBusinessTitle, getBusinessLocation, formatTo24HourTime, bookingColumns, bookingGhostRows } from '../../utils/appBookingHelpers';

const AppBookingsTable = ({
    bookings = [],
    loading = false,
    activeTabFilter,
    setActiveTabFilter,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    itemsPerPage = 10,
    onSelectBooking
}) => {
    // Filter bookings based on active tab and status dropdown
    const filteredBookings = bookings.filter((b) => {
        let tabMatch = true;
        if (activeTabFilter === 'restaurant') tabMatch = b.type === 'Restaurant';
        else if (activeTabFilter === 'activity') tabMatch = b.type === 'Task' || b.type === 'Activity';
        else if (activeTabFilter === 'shop') tabMatch = b.type === 'Shop';

        let statusMatch = true;
        if (statusFilter !== 'all') {
            statusMatch = (b.status || '').toLowerCase() === statusFilter.toLowerCase();
        }

        return tabMatch && statusMatch;
    });

    const totalItems = filteredBookings.length;
    const maxPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const startIdx = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endIdx = Math.min(currentPage * itemsPerPage, totalItems);
    const paginatedBookings = filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="bg-white rounded-2xl border border-slate-100/90 shadow-xs overflow-hidden">
            {/* Tabs & Status Filter Bar */}
            <div className="px-6 pt-5 pb-0 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-6 overflow-x-auto overflow-y-hidden no-scrollbar w-full md:w-auto">
                    {appBookingsData.tabs.map((tab) => {
                        const isAll = tab.id === 'all';
                        const isDisabled = !isAll && bookings.length === 0 && !loading;
                        const isActive = activeTabFilter === tab.id;

                        return (
                            <button
                                key={tab.id}
                                disabled={isDisabled}
                                onClick={() => {
                                    if (!isDisabled) {
                                        setActiveTabFilter(tab.id);
                                        setCurrentPage(1);
                                    }
                                }}
                                className={`text-sm font-medium pb-3 -mb-[1px] transition-all whitespace-nowrap ${isDisabled
                                    ? 'text-slate-300 cursor-not-allowed select-none'
                                    : isActive
                                        ? 'text-[#067978] font-bold border-b-2 border-[#067978] cursor-pointer'
                                        : 'text-slate-500 hover:text-slate-700 cursor-pointer'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-3 mb-3">
                    <div className="w-44">
                        <CustomSelect
                            value={statusFilter}
                            onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}
                            disabled={bookings.length === 0 && !loading}
                            options={appBookingsData.statuses}
                            icon={Filter}
                            placeholder="All Statuses"
                        />
                    </div>
                </div>
            </div>

            {/* Table Content */}
            {loading ? (
                <TableSkeleton columns={bookingColumns} rows={5} cardWrapper={false} />
            ) : bookings.length === 0 && activeTabFilter === 'all' && statusFilter === 'all' ? (
                <EmptyStateTable
                    title="No app bookings yet"
                    description="When your travellers book partner services — dining, activities, and experiences — inside your branded mobile app, their reservations and your commission earnings will appear here in real time."
                    columns={bookingColumns}
                    rows={bookingGhostRows}
                    renderRow={(row) => (
                        <div
                            className={`hidden md:grid grid-cols-12 gap-4 items-center px-6 py-3.5 bg-white border-b border-slate-100 ${row.isSample ? 'hover:bg-slate-50/50' : `${row.opacity || 'opacity-30'} select-none pointer-events-none`
                                }`}
                        >
                            <div className="col-span-2">
                                <p className="text-slate-800 text-sm font-normal">{row.guest}</p>
                                <p className="text-[11px] text-slate-400 font-normal">Guest</p>
                            </div>
                            <div className="col-span-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-full bg-emerald-50 text-[#067978] flex items-center justify-center font-normal text-xs shrink-0">
                                        {row.partner.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-slate-800 text-sm font-normal truncate">{row.partner}</p>
                                        <div className="flex items-center gap-1 text-[11px] text-slate-400 font-normal mt-0.5">
                                            <MapPin size={10} className="text-[#067978] shrink-0" />
                                            <span>{row.partnerCity}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-2 text-sm text-slate-600 font-normal font-mono whitespace-nowrap">{row.bookedFor}</div>
                            <div className="col-span-2 flex items-center">
                                <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-full uppercase tracking-wider inline-block bg-teal-50 text-[#067978] border border-teal-100/70 whitespace-nowrap">
                                    {row.type}
                                </span>
                            </div>
                            <div className="col-span-1 text-sm font-normal text-slate-800 font-mono whitespace-nowrap">{row.billAmount}</div>
                            <div className="col-span-1 text-sm font-normal text-emerald-600 font-mono whitespace-nowrap">{row.commission}</div>
                            <div className="col-span-1 flex justify-center">
                                <span className="inline-flex items-center gap-1.5 text-xs font-normal text-emerald-600 whitespace-nowrap">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                    {row.status}
                                </span>
                            </div>
                        </div>
                    )}
                    renderMobileRow={(row) => (
                        <div className={`bg-white border rounded-2xl p-3.5 ${row.isSample ? 'border-slate-200/90 shadow-xs' : 'border-slate-100 opacity-40 select-none pointer-events-none'}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-slate-800 text-sm font-normal">{row.guest}</h4>
                                    <p className="text-[11px] text-slate-400 font-normal">{row.partner} · {row.bookedFor}</p>
                                </div>
                                <span className="inline-flex items-center text-xs font-normal text-emerald-600">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                                    {row.status}
                                </span>
                            </div>
                        </div>
                    )}
                />
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[900px]">
                            <thead>
                                <tr className="border-b border-slate-100 text-xs text-slate-400 font-medium">
                                    <th className="py-3.5 px-6 font-medium">Guest</th>
                                    <th className="py-3.5 px-6 font-medium">Partner</th>
                                    <th className="py-3.5 px-6 font-medium">Booked For</th>
                                    <th className="py-3.5 px-6 font-medium">Type</th>
                                    <th className="py-3.5 px-6 font-medium">Bill Amount</th>
                                    <th className="py-3.5 px-6 font-medium">Commission</th>
                                    <th className="py-3.5 px-6 font-medium">Status</th>
                                    <th className="py-3.5 px-4 font-medium text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {paginatedBookings.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="py-16 px-4 text-center bg-slate-50/30">
                                            <ShoppingBag size={40} className="mx-auto text-slate-300 mb-3" />
                                            <h3 className="text-base font-normal text-slate-700">No Bookings Found</h3>
                                            <p className="text-xs text-slate-400 mt-1 mb-4 font-normal">No bookings match your selected tab or status filter.</p>
                                            <button
                                                onClick={() => { setActiveTabFilter('all'); setStatusFilter('all'); setCurrentPage(1); }}
                                                className="text-[#067978] font-normal text-xs hover:underline uppercase tracking-wider cursor-pointer"
                                            >
                                                Reset Filters
                                            </button>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedBookings.map((booking) => {
                                        const bookedTime24 = formatTo24HourTime(booking.bookedTime);
                                        return (
                                            <tr
                                                key={booking._id}
                                                className="hover:bg-slate-50/60 transition-colors text-sm border-b border-slate-100 cursor-pointer"
                                                onClick={() => onSelectBooking(booking)}
                                            >
                                                <td className="px-6 py-3.5">
                                                    <div>
                                                        <p className="font-normal text-slate-800 text-sm">{booking.guest_name || booking.guestName || booking.booking_id?.name || 'App User'}</p>
                                                        <p className="text-xs text-slate-400 font-normal">Guest</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3.5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-emerald-50 text-[#067978] font-normal text-sm flex items-center justify-center shrink-0">
                                                            {(getBusinessTitle(booking) || 'P').charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <p className="font-normal text-slate-800 text-sm hover:text-[#067978] transition-colors truncate max-w-[200px]">
                                                                {getBusinessTitle(booking)}
                                                            </p>
                                                            <div className="flex items-center gap-1 text-xs text-slate-400 font-normal mt-0.5">
                                                                <MapPin size={10} className="text-[#067978]" />
                                                                {getBusinessLocation(booking)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3.5 whitespace-nowrap">
                                                    <div>
                                                        <p className="font-normal text-slate-700 text-sm flex items-center gap-1.5">
                                                            <CalendarIcon size={12} className="text-slate-400" />
                                                            {new Date(booking.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </p>
                                                        {bookedTime24 && (
                                                            <p className="text-xs text-slate-400 font-normal flex items-center gap-1.5 mt-0.5 font-mono">
                                                                <Clock size={12} className="text-slate-400" />
                                                                {bookedTime24}
                                                            </p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3.5">
                                                    <span className="px-2.5 py-0.5 text-xs font-medium rounded-full inline-block bg-teal-50 text-[#067978] border border-teal-100/70">
                                                        {booking.type === 'Task' ? 'Activity' : booking.type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3.5">
                                                    <span className="font-normal text-slate-800 text-sm font-mono">
                                                        ₹{(booking.final_bill_amount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3.5">
                                                    <span className="font-normal text-emerald-600 text-sm font-mono">
                                                        + ₹{(booking.computed_agent_cut || 0).toLocaleString('en-IN')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3.5 whitespace-nowrap">
                                                    {(() => {
                                                        const s = (booking.status || '').toLowerCase();
                                                        if (s === 'completed' || s === 'confirmed') {
                                                            return (
                                                                <span className="inline-flex items-center gap-1.5 text-sm font-normal text-emerald-600">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                                    {booking.status}
                                                                </span>
                                                            );
                                                        }
                                                        if (s === 'pending') {
                                                            return (
                                                                <span className="inline-flex items-center gap-1.5 text-sm font-normal text-amber-500">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                                                    {booking.status}
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <span className="inline-flex items-center gap-1.5 text-sm font-normal text-rose-500">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                                                {booking.status}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-4 py-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                                                    <BookingActions onViewDetails={() => onSelectBooking(booking)} />
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="px-6 py-4 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-slate-500 font-normal">
                        <div>
                            Showing {startIdx} to {endIdx} of {totalItems} bookings
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                className="p-1.5 rounded border border-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={14} />
                            </button>

                            {Array.from({ length: Math.min(maxPages, 5) }).map((_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`px-2.5 py-1 rounded text-xs font-semibold ${currentPage === pageNum
                                            ? 'border border-[#067978] text-[#067978] font-bold'
                                            : 'text-slate-600 hover:bg-slate-50'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            <button
                                disabled={currentPage >= maxPages}
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPages))}
                                className="p-1.5 rounded border border-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AppBookingsTable;
